/**
 * Helpers for Market Overview multi-line overlay chart.
 */

import { isYahooCryptoTicker } from '../lib/yahooFinance/symbols';

const DAY_MINUTES = 24 * 60;
const NY_TZ = 'America/New_York';

export { isYahooCryptoTicker };

function formatNyYmd(unixSeconds) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: NY_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(Number(unixSeconds) * 1000));
}

/**
 * Unix seconds at 12:00 AM America/New_York for the calendar day of `unixSeconds`.
 * Intraday overlays start here so every series begins at 0% at midnight.
 */
export function startOfNewYorkDayUnix(unixSeconds) {
  const ts = Number(unixSeconds);
  if (!Number.isFinite(ts)) return null;
  const ymd = formatNyYmd(ts);
  const utcMidnight = Date.parse(`${ymd}T00:00:00Z`);
  if (!Number.isFinite(utcMidnight)) return null;
  for (let hours = 3; hours <= 6; hours += 1) {
    const candidateMs = utcMidnight + hours * 3600 * 1000;
    if (formatNyYmd(candidateMs / 1000) !== ymd) continue;
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: NY_TZ,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(candidateMs));
    const hour = Number(parts.find((p) => p.type === 'hour')?.value);
    const minute = Number(parts.find((p) => p.type === 'minute')?.value);
    if (hour === 0 && minute === 0) return Math.floor(candidateMs / 1000);
  }
  return Math.floor(utcMidnight / 1000);
}

/**
 * 24/7 symbols need enough intraday bars to cover the equity session plus
 * overnight/weekend continuation. Last-N clipping otherwise keeps only Sat/Sun.
 */
export function overlayBarsbackForSymbol(symbol, tf) {
  const base = Number(tf?.barsback);
  if (!Number.isFinite(base) || base <= 0) return tf?.barsback;
  if (tf?.unit !== 'Minute' || !isYahooCryptoTicker(symbol)) return base;
  const minutes = Number(tf.interval) || 5;
  return Math.max(base, Math.ceil((7 * DAY_MINUTES) / minutes));
}

function seriesStart(bars) {
  return bars?.[0]?.time;
}

function seriesEnd(bars) {
  return bars?.[bars.length - 1]?.time;
}

/**
 * Time window of session (non-24/7) series. End is the last equity print;
 * 24/7 series may continue after that (overnight / weekend) on their own.
 */
export function findSessionOverlayRange(barsBySymbol, { sessionSymbols, minBars = 5 } = {}) {
  const pick = (predicate) => Object.entries(barsBySymbol || {})
    .filter(([symbol, bars]) => bars?.length >= minBars && predicate(symbol))
    .map(([, bars]) => bars);

  let entries = [];
  if (Array.isArray(sessionSymbols) && sessionSymbols.length) {
    const sessionSet = new Set(sessionSymbols);
    entries = pick((symbol) => sessionSet.has(symbol));
  }
  if (!entries.length) {
    entries = pick((symbol) => !isYahooCryptoTicker(symbol));
  }
  if (!entries.length) {
    entries = pick(() => true);
  }
  if (!entries.length) return null;

  let start = null;
  let end = null;
  entries.forEach((bars) => {
    const first = seriesStart(bars);
    const last = seriesEnd(bars);
    if (first == null || last == null) return;
    if (start == null || first < start) start = first;
    if (end == null || last > end) end = last;
  });
  return start == null ? null : { start, end };
}

/**
 * Keep bars after `start`. When `end` is set, also drop later bars.
 * Series with no overlap become empty so they cannot plot on a different region.
 */
export function trimBarsToTimeRange(barsBySymbol, range) {
  if (!range || range.start == null) return barsBySymbol || {};
  const out = {};
  Object.entries(barsBySymbol || {}).forEach(([symbol, bars]) => {
    const list = bars || [];
    const trimmed = list.filter((b) => {
      if (b?.time == null) return false;
      if (b.time < range.start) return false;
      if (range.end != null && b.time > range.end) return false;
      return true;
    });
    out[symbol] = trimmed.length >= 2 ? trimmed : [];
  });
  return out;
}

/**
 * Minute overlays: drop bars before 12:00 AM ET of the equity session day.
 * Bitcoin keeps overnight/weekend prints after equities stop.
 * Daily overlays: shared start so every line begins together.
 */
export function alignMarketOverviewBars(barsBySymbol, tf) {
  if (tf?.unit === 'Minute') {
    const sessionSymbols = Object.keys(barsBySymbol || {}).filter((s) => !isYahooCryptoTicker(s));
    const sessionRange = findSessionOverlayRange(barsBySymbol, {
      sessionSymbols,
      minBars: tf.minBars || 5,
    });
    if (!sessionRange) return barsBySymbol || {};
    const start = startOfNewYorkDayUnix(sessionRange.start) ?? sessionRange.start;
    return trimBarsToTimeRange(barsBySymbol, { start, end: null });
  }
  const commonStart = findCommonStartTime(barsBySymbol, { minBars: tf.minBars || 5 });
  return trimBarsToCommonStart(barsBySymbol, commonStart);
}

/**
 * Find a shared start so overlay lines begin together.
 * Uses the latest first-bar among series that have enough history.
 * Sparse/short series (failed fetch, thin session) are ignored so they
 * cannot collapse everyone to a single point (LineSeries needs ≥2 points).
 *
 * @param {Record<string, Array<{time:any}>>} barsBySymbol
 * @param {{ minBars?: number }} [opts]
 */
export function findCommonStartTime(barsBySymbol, { minBars = 5 } = {}) {
  const entries = Object.values(barsBySymbol || {}).filter((bars) => bars?.length);
  if (!entries.length) return null;

  const maxLen = Math.max(...entries.map((bars) => bars.length));
  const threshold = Math.max(minBars, Math.floor(maxLen * 0.5));

  let commonStart = null;
  entries.forEach((bars) => {
    if (bars.length < threshold) return;
    const first = bars[0].time;
    if (first == null) return;
    if (commonStart == null || first > commonStart) {
      commonStart = first;
    }
  });

  // Fallback: if nothing met the threshold, use the earliest first bar so we
  // still draw whatever we have instead of trimming to emptiness.
  if (commonStart == null) {
    entries.forEach((bars) => {
      const first = bars[0].time;
      if (first == null) return;
      if (commonStart == null || first < commonStart) {
        commonStart = first;
      }
    });
  }

  return commonStart;
}

/**
 * Drop bars before the shared start so overlay lines begin on the same x.
 * If trimming would leave a series with fewer than 2 points (nothing to draw),
 * keep the original bars for that symbol.
 */
export function trimBarsToCommonStart(barsBySymbol, commonStart) {
  if (commonStart == null) return barsBySymbol || {};
  const out = {};
  Object.entries(barsBySymbol || {}).forEach(([symbol, bars]) => {
    const list = bars || [];
    const trimmed = list.filter((b) => b.time >= commonStart);
    out[symbol] = trimmed.length >= 2 ? trimmed : list;
  });
  return out;
}

/**
 * Deduplicate bars that share a timestamp (keep last). Lightweight Charts
 * rejects duplicate times on setData.
 */
export function dedupeBarsByTime(bars) {
  if (!Array.isArray(bars) || bars.length < 2) return bars || [];
  const byTime = new Map();
  bars.forEach((b) => {
    if (b?.time == null) return;
    byTime.set(b.time, b);
  });
  return Array.from(byTime.values()).sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));
}

/**
 * Normalize close series to % change.
 * @param {Array<{time: any, close: number}>} bars
 * @param {number|null} baseline - when set (e.g. previous close for 1D), use it; else first bar close
 */
export function normalizeBarsToPercent(bars, baseline = null) {
  if (!bars?.length) return [];
  const base = baseline || bars[0].close;
  if (!base) return [];
  return bars.map((bar) => ({
    time: bar.time,
    value: ((bar.close - base) / base) * 100,
  }));
}

function compareTime(a, b) {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/**
 * Equal-weighted average of enabled normalized series.
 * Each series holds its last value, so a bar that exists on only one symbol
 * (overnight Bitcoin, or a print a few seconds off the others) cannot yank the
 * line to that symbol alone. The line starts once every included series has
 * printed.
 */
export function computeAverageSeries(normalizedBySymbol, enabledSymbols = {}) {
  const seriesList = Object.entries(normalizedBySymbol || {})
    .filter(([symbol]) => enabledSymbols[symbol] !== false)
    .map(([, data]) => (Array.isArray(data) ? data : [])
      .filter((point) => point?.time != null && Number.isFinite(point.value))
      .sort((a, b) => compareTime(a.time, b.time)))
    .filter((data) => data.length > 0);

  if (!seriesList.length) return [];

  const timeSet = new Set();
  seriesList.forEach((series) => {
    series.forEach((point) => timeSet.add(point.time));
  });
  const times = Array.from(timeSet).sort(compareTime);

  const cursors = seriesList.map(() => 0);
  const lastValues = seriesList.map(() => null);
  const out = [];

  times.forEach((time) => {
    let sum = 0;
    let count = 0;
    for (let i = 0; i < seriesList.length; i += 1) {
      const series = seriesList[i];
      while (cursors[i] < series.length && compareTime(series[cursors[i]].time, time) <= 0) {
        lastValues[i] = series[cursors[i]].value;
        cursors[i] += 1;
      }
      if (lastValues[i] != null) {
        sum += lastValues[i];
        count += 1;
      }
    }
    // Skip the prefix where only a subset has printed (Bitcoin overnight).
    if (count < seriesList.length) return;
    out.push({ time, value: sum / count });
  });

  return out;
}

/**
 * Right-edge percent of a normalized series — the same number the line ends on.
 * Quote NetChangePct is the broker daily bar, which rolls onto a new candle
 * (often after the equity close) and no longer matches this window.
 */
export function seriesEndPercent(points) {
  if (!Array.isArray(points) || points.length === 0) return null;
  const value = points[points.length - 1]?.value;
  return Number.isFinite(value) ? value : null;
}
